import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Vault, Receipt, GitCompareArrows, FileCheck, CreditCard, FolderOpen,
  CalendarDays, BarChart3, Archive, LifeBuoy, Settings, Bell, LogOut, Menu, X, Users, ListTodo,
  Percent, Crown, Plug, DollarSign, ScrollText, Search, Briefcase, Landmark, ArrowLeftRight } from "lucide-react";
import { useAuth, STAFF_ROLES, ADMIN_ROLES } from "../context/AuthContext";
import { API, errMsg } from "../lib/api";
import { Modal, Field, ErrBox } from "./common";
import { toast } from "sonner";
import { Building2, ChevronDown, PlusCircle } from "lucide-react";

const PORTAL_MENU = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/vault", label: "Tax Vault", icon: Vault },
  { to: "/portal/sales", label: "Sales", icon: Receipt },
  { to: "/portal/reconciliation", label: "Reconciliation", icon: GitCompareArrows },
  { to: "/portal/filings", label: "Filings & Periods", icon: FileCheck },
  { to: "/portal/payments", label: "Payments", icon: CreditCard },
  { to: "/portal/documents", label: "Document Vault", icon: FolderOpen },
  { to: "/portal/calendar", label: "Tax Calendar", icon: CalendarDays },
  { to: "/portal/reports", label: "Reports", icon: BarChart3 },
  { to: "/portal/year-end", label: "Year-End Center", icon: Archive },
  { to: "/portal/support", label: "Support", icon: LifeBuoy },
  { to: "/portal/settings", label: "Settings", icon: Settings },
];

const ADMIN_MENU = [
  { to: "/admin", label: "Command Center", icon: Landmark, end: true },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/work-queue", label: "Work Queue", icon: ListTodo },
  { to: "/admin/rescue", label: "Tax Rescue Cases", icon: LifeBuoy },
  { to: "/admin/tax-rules", label: "Tax Rules", icon: Percent, adminOnly: true },
  { to: "/admin/plans", label: "Plans & Pricing", icon: Crown, adminOnly: true },
  { to: "/admin/integrations", label: "Integrations", icon: Plug },
  { to: "/admin/revenue", label: "Revenue", icon: DollarSign, adminOnly: true },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText, adminOnly: true },
  { to: "/admin/settings", label: "Settings", icon: Settings, adminOnly: true },
];

const TAXPRO_MENU = [
  { to: "/taxpro", label: "My Dashboard", icon: Briefcase, end: true },
  { to: "/admin/customers", label: "Clients", icon: Users },
  { to: "/admin/work-queue", label: "Work Queue", icon: ListTodo },
  { to: "/admin/rescue", label: "Tax Rescue Cases", icon: LifeBuoy },
];

function menuFor(user) {
  if (!user) return [];
  if (user.role === "TAX_PROFESSIONAL") return TAXPRO_MENU;
  if (STAFF_ROLES.includes(user.role)) {
    const isAdmin = ADMIN_ROLES.includes(user.role);
    return ADMIN_MENU.filter((m) => !m.adminOnly || isAdmin);
  }
  return PORTAL_MENU;
}

export default function PortalLayout() {
  const { user, business, businesses, activeBusinessId, switchBusiness, reload,
    impersonating, logout, stopImpersonating } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [bizMenu, setBizMenu] = useState(false);
  const [addBizOpen, setAddBizOpen] = useState(false);
  const [bizForm, setBizForm] = useState({ legal_name: "", dba: "", ein: "", entity_type: "LLC", tpt_license: "" });
  const [bizErr, setBizErr] = useState("");
  const [bizBusy, setBizBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    API.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!search || search.length < 2 || !STAFF_ROLES.includes(user?.role)) { setResults(null); return; }
    const t = setTimeout(() => {
      API.get(`/admin/search?q=${encodeURIComponent(search)}`).then((r) => setResults(r.data.results)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search, user]);

  if (user === null) return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  const menu = menuFor(user);
  const isStaff = STAFF_ROLES.includes(user.role);
  const canMultiBiz = ["BUSINESS_OWNER", "BUSINESS_MANAGER"].includes(user.role);

  const addBusiness = async (e) => {
    e.preventDefault();
    setBizBusy(true); setBizErr("");
    try {
      const { data } = await API.post("/businesses", bizForm);
      toast.success(`Business ${data.case_number} added`);
      setAddBizOpen(false);
      setBizForm({ legal_name: "", dba: "", ein: "", entity_type: "LLC", tpt_license: "" });
      await reload();
      await switchBusiness(data.business_id);
      window.location.reload();
    } catch (e2) { setBizErr(errMsg(e2)); }
    setBizBusy(false);
  };

  return (
    <div className="flex min-h-screen bg-slate-100">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 transform border-r border-slate-200 bg-white transition-transform md:static md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="seal-strip" />
        <div className="flex items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="portal-logo">
            <img src="/logo.png" alt="" className="h-9 w-9 rounded-full" />
            <div className="leading-tight">
              <div className="text-sm font-extrabold text-navy">AZ <span className="text-copper">TAX</span> ACCOUNTS</div>
              <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">
                {isStaff ? (user.role === "TAX_PROFESSIONAL" ? "Tax Professional" : "Administration") : "Business Portal"}
              </div>
            </div>
          </Link>
          <button className="text-slate-400 md:hidden" onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-5 w-5" /></button>
        </div>
        <nav className="mt-2 space-y-0.5 px-2 pb-24">
          {menu.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.end} onClick={() => setOpen(false)}
              data-testid={`menu-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-copper-50 font-semibold text-copper" : "text-slate-600 hover:bg-slate-100 hover:text-navy"}`}>
              <m.icon className="h-4 w-4" /> {m.label}
            </NavLink>
          ))}
          {isStaff && (
            <NavLink to="/portal" data-testid="menu-customer-portal"
              className="mt-4 flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100">
              <ArrowLeftRight className="h-4 w-4" /> Customer Portal View
            </NavLink>
          )}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setOpen(true)} data-testid="portal-mobile-menu" aria-label="Menu"><Menu className="h-5 w-5 text-navy" /></button>
            {business && (
              <div className="relative hidden sm:block" data-testid="business-identity">
                <button onClick={() => canMultiBiz && businesses.length > 0 && setBizMenu(!bizMenu)}
                  className={`flex items-center gap-2 rounded-md px-2 py-1 ${canMultiBiz ? "hover:bg-slate-100" : ""}`}
                  data-testid="business-switcher">
                  <span className="text-sm font-bold text-navy">{business.dba || business.legal_name}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">{business.case_number}</span>
                  {business.is_demo && <span className="rounded bg-gold/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700" data-testid="demo-badge">Demo</span>}
                  {canMultiBiz && <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
                </button>
                {bizMenu && (
                  <div className="absolute left-0 top-10 z-50 w-72 rounded-md border border-slate-200 bg-white shadow-lg" data-testid="business-switcher-menu">
                    <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Your Businesses</div>
                    {businesses.map((b) => (
                      <button key={b.id} onClick={() => { setBizMenu(false); if (b.id !== activeBusinessId) { switchBusiness(b.id).then(() => window.location.reload()); } }}
                        className={`flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-left hover:bg-slate-50 ${b.id === activeBusinessId ? "bg-navy-50" : ""}`}
                        data-testid={`switch-to-${b.case_number}`}>
                        <Building2 className="h-4 w-4 text-navy" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-800">{b.dba || b.legal_name}</div>
                          <div className="font-mono text-[10px] text-slate-400">{b.case_number}</div>
                        </div>
                        {b.id === activeBusinessId && <span className="ml-auto text-[10px] font-bold text-emerald-600">ACTIVE</span>}
                      </button>
                    ))}
                    <button onClick={() => { setBizMenu(false); setAddBizOpen(true); setBizErr(""); }}
                      className="flex w-full items-center gap-2 border-t border-slate-200 px-3 py-2.5 text-sm font-semibold text-copper hover:bg-copper-50"
                      data-testid="add-business-btn">
                      <PlusCircle className="h-4 w-4" /> Add Another Business
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isStaff && (
              <div className="relative hidden lg:block">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients, cases, confirmations…"
                  className="w-72 rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-navy focus:outline-none" data-testid="global-search" />
                {results && (
                  <div className="absolute right-0 top-11 w-80 rounded-md border border-slate-200 bg-white shadow-lg" data-testid="global-search-results">
                    {results.length === 0 && <div className="px-4 py-3 text-sm text-slate-500">No matches</div>}
                    {results.map((r) => (
                      <button key={r.type + r.id} onClick={() => { navigate(r.link); setSearch(""); setResults(null); }}
                        className="block w-full border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50">
                        <div className="text-sm font-semibold text-navy">{r.label}</div>
                        <div className="text-xs text-slate-500">{r.type.replace(/_/g, " ")} · {r.sub}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Link to={isStaff && user.role !== "TAX_PROFESSIONAL" ? "/admin" : "/portal/notifications"} className="relative rounded-md p-2 text-slate-500 hover:bg-slate-100" data-testid="notifications-bell" aria-label="Notifications">
              <Bell className="h-5 w-5" />
              {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-copper px-1 text-[10px] font-bold text-white">{unread}</span>}
            </Link>
            <div className="hidden text-right sm:block">
              <div className="text-xs font-semibold text-slate-800">{user.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{user.role.replace(/_/g, " ")}</div>
            </div>
            <button onClick={async () => { await logout(); navigate("/"); }} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" data-testid="logout-btn" aria-label="Log out">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>

        {impersonating && (
          <div className="flex items-center justify-between bg-gold px-4 py-2 text-sm font-semibold text-navy-dark" data-testid="impersonation-banner">
            <span>You are viewing this account as the customer. Actions are audit-logged.</span>
            <button onClick={stopImpersonating} className="rounded bg-navy-dark px-3 py-1 text-xs text-white" data-testid="stop-impersonation-btn">Return to Admin</button>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl"><Outlet /></div>
        </main>

        <nav className="sticky bottom-0 z-30 flex justify-around border-t border-slate-200 bg-white py-2 md:hidden" data-testid="mobile-quickbar">
          {menu.slice(0, 4).map((m) => (
            <NavLink key={m.to} to={m.to} end={m.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-semibold ${isActive ? "text-copper" : "text-slate-500"}`}>
              <m.icon className="h-5 w-5" />{m.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>
      </div>

      <Modal open={addBizOpen} onClose={() => setAddBizOpen(false)} title="Add Another Business">
        <form onSubmit={addBusiness} className="space-y-4" data-testid="add-business-form">
          <p className="text-sm text-slate-600">Each business gets its own dashboard, Tax Vault, filings, documents, and subscription. Switch between them from the top bar.</p>
          <Field label="Legal Business Name *"><input className="input-gov" value={bizForm.legal_name} onChange={(e) => setBizForm({ ...bizForm, legal_name: e.target.value })} required data-testid="ab-legal-name" /></Field>
          <Field label="DBA"><input className="input-gov" value={bizForm.dba} onChange={(e) => setBizForm({ ...bizForm, dba: e.target.value })} data-testid="ab-dba" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="EIN *" hint="Required — stored securely and masked"><input className="input-gov font-mono" placeholder="12-3456789" value={bizForm.ein} onChange={(e) => setBizForm({ ...bizForm, ein: e.target.value })} required data-testid="ab-ein" /></Field>
            <Field label="Entity Type">
              <select className="input-gov" value={bizForm.entity_type} onChange={(e) => setBizForm({ ...bizForm, entity_type: e.target.value })} data-testid="ab-entity">
                {["LLC", "S-Corp", "C-Corp", "Sole Proprietor", "Partnership", "Non-Profit"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Arizona TPT License"><input className="input-gov" value={bizForm.tpt_license} onChange={(e) => setBizForm({ ...bizForm, tpt_license: e.target.value })} data-testid="ab-tpt" /></Field>
          <ErrBox msg={bizErr} />
          <button className="btn-copper w-full" disabled={bizBusy} data-testid="ab-submit">{bizBusy ? "Creating…" : "Add Business"}</button>
        </form>
      </Modal>
    </div>
  );
}
